import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import { useUserRole } from "@/services/userServices/useUserRole";
import React, { useEffect, useState, Fragment } from "react";

import { ToastContainer } from "react-toastify";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { Button } from "reactstrap";
import { Link } from "react-router";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import NotificationAlertOptions from "@/layouts/Alerts";
import ReferrersDialog from "@/components/ReferrersDialog";
import { isDeleteRefusal, refusalMessage } from "@/services/deleteRefusal";
import { ADMIN } from "@/routes/Roles";
import {
  addBlog,
  getBlog,
  updateBlog,
  getPosts,
  changeStatus,
  updatePostStatus,
  deleteBlog,
  clearMessage,
} from "@/services/blogServices/blogActions";

import SiteBlogView from "./SiteBlogView";
import { asCaught } from "@/services/caughtError";

export default function SiteBlogContainer() {
  const dispatch = useAppDispatch();

 
  const blog = useAppSelector((s) => s.blog);
  const errors = useAppSelector((s) => s.errors);
  const permission = useCurrentPermission();
  const profile = useAppSelector((s) => s.profile?.profile);

  
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [viewAuthors, setViewAuthors] = useState(false);
  const [author, setAuthor] = useState<any>({}); // any: a post's populated author, read off the blog slice's posts, typed any[] there (blogSlice.ts:31)
  const [alertNode, setAlertNode] = useState<React.ReactNode>(null);

  const [validation, setValidation] = useState({ title: true });

  const userRole = useUserRole();

  // SCRUM-951: "loaded and empty" — true only after getBlog() returns []. NOT
  // the initial {} (loading / never fetched, or the instant after DELETE_BLOG
  // before its refetch) and NOT the populated [{…}] case. Deriving it from the
  // array shape rather than viewAddButton avoids showing the banner during load
  // (viewAddButton is set true on the truthy {} first render — the ticket's trap).
  const hasNoBlog = Array.isArray(blog?.blog) && blog.blog.length === 0;

  // SCRUM-2222: which button shows, and the stored blog's id, are read off the
  // store during render. An effect used to copy them into state. Add shows for
  // [] and for the initial {} alike, as it did; neither shows before the slice
  // holds anything.
  const storedBlogs = blog?.blog;
  const hasStoredBlog = Array.isArray(storedBlogs)
    ? storedBlogs.length > 0
    : !!storedBlogs && Object.keys(storedBlogs).length > 0;
  const storedBlog = Array.isArray(storedBlogs) ? storedBlogs[0] : storedBlogs;
  const viewAddButton = !!storedBlogs && !hasStoredBlog;
  const viewUpdateButton = hasStoredBlog;
  const _id = hasStoredBlog ? storedBlog?._id || "" : "";

  useEffect(() => {
    dispatch(getBlog());
    dispatch(getPosts());
  }, [dispatch]);

  
  // SCRUM-1150: the search moved to TableWrapper's global filter and the paging
  // to react-table, but the ROLE FILTER stays here and feeds `data` — SCRUM-1026
  // is explicit that it is a feature rather than list chrome. Handing TableWrapper
  // pre-sorted rows keeps the previous default order (`_id` descending) while the
  // headers additionally become sortable. (No useMemo: the React Compiler
  // memoises this component, SCRUM-2222.)
  let filteredPosts = blog?.posts || [];
  if (userRole !== ADMIN && profile?.name) {
    filteredPosts = filteredPosts.filter(
      (p) =>
        p?.author?.name &&
        p.author.name.toLowerCase() === profile.name.toLowerCase()
    );
  }
  const sourcePosts = [...filteredPosts].sort((a, b) =>
    a._id > b._id ? -1 : b._id > a._id ? 1 : 0
  );

  // Seeds the form fields from the stored blog whenever the store's copy
  // changes; the editor's typing lives in state between those changes.
  useEffect(() => {
    const b = blog?.blog;
    if (!b) return;
    const isEmptyArray = Array.isArray(b) && b.length === 0;
    const isEmptyObj = !Array.isArray(b) && Object.keys(b).length === 0;
    const item = isEmptyArray || isEmptyObj ? null : Array.isArray(b) ? b[0] : b;
    setTitle(item?.title || "");
    setDescription(item?.description || "");
  }, [blog?.blog]);

  useEffect(() => {
  
    if (errors?.errors && Array.isArray(errors.errors) && errors.errors.length) {
      const v = { ...validation };
      errors.errors.forEach((er) => {
        if (er?.value === "" && er?.param) v[er.param] = false;
      });
      setValidation(v);
    }
  }, [errors, dispatch]); 

  useEffect(() => {
   
    if (blog?.message) {
      if (blog.message !== "") {
        NotificationAlertOptions("success", blog.message);
        dispatch(clearMessage());
      }
    }
  }, [blog?.message, dispatch]);

  const onChangeTitle = (e) => {
    setTitle(e.target.value);
    setValidation((v) => ({ ...v, title: true }));
  };

  const onChangeDescription = (e) => setDescription(e.target.value);

  const onClickUpdate = (e) => {
    e.preventDefault();
    dispatch(updateBlog({ title, description, _id: _id || undefined }));
  };

  const onClickAdd = () => {
    dispatch(addBlog({ title, description }));
  };

  // SCRUM-1150: react-table owns row order and paging now, so the index that used
  // to be the position inside the page slice is resolved against the source array
  // instead. The reducer keys CHANGE_STATUS on `element._id` (blogSlice.js:91-96)
  // and never reads the index, so this is inert either way — but a render-order
  // index would be wrong the moment anything did read it.
  const onClickCheckBox = async (e, element) => {
    const active = !element.active;
    const updated = { ...element, active };
    const index = sourcePosts.findIndex((p) => p._id === element._id);

    dispatch(
      changeStatus({
        index,
        element: updated,
      })
    );

    // Persist the toggle; revert the optimistic show/hide if the server rejects
    // (updatePostStatus now re-throws on failure — SCRUM-815).
    try {
      await dispatch(updatePostStatus({ _id: element._id, active }));
    } catch (err) {
      dispatch(changeStatus({ index, element }));
      NotificationAlertOptions(
        "error",
        asCaught(err)?.response?.data?.message || "Updating the post status failed. Please try again."
      );
    }
  };

  const viewAuthorInformation = (a) => {
    setAuthor(a || {});
    setViewAuthors(true);
  };

  const closeViewAuthor = () => setViewAuthors(false);

  const hideAlert = () => setAlertNode(null);

  // SCRUM-2355: the SCRUM-2352 pop-up for a delete the server refused.
  const showReferrers = (err: unknown) => {
    if (!isDeleteRefusal(err)) return;
    setAlertNode(
      <ReferrersDialog
        message={refusalMessage(err)}
        referrers={err.referrers}
        onClose={hideAlert}
      />
    );
  };

  const questionAlert = () => {
    setAlertNode(
      // @ts-expect-error react-bootstrap-sweetalert prop-types
      <ReactBSAlert
        custom
        style={{ display: "block", marginTop: "-100px" }}
        title="Would you like to remove this Blog?"
        customIcon={
          <div
            className="swal2-icon swal2-question swal2-animate-question-icon"
            style={{ display: "flex" }}
          >
            <span className="swal2-icon-text">?</span>
          </div>
        }
        onConfirm={async () => {
          // SCRUM-1021 follow-up: deleteBlog is a createAppAsyncThunk now, so await
          // .unwrap() and clear the form + toast success ONLY after it resolves. A
          // failure rejects and is surfaced once centrally by appErrorMiddleware —
          // a local error toast here would double it (matches the other delete flows).
          try {
            if (_id) {
              await dispatch(deleteBlog(_id)).unwrap();
            }
            setTitle("");
            setDescription("");
            hideAlert();
            NotificationAlertOptions("success", "Blog Deleted Successfully");
          } catch (err) {
            hideAlert();
            // SCRUM-2355: a refusal with referrers (the blog's posts) gets the
            // list; every other failure is surfaced by the hub.
            showReferrers(err);
          }
        }}
        onCancel={hideAlert}
        confirmBtnBsStyle="default"
        confirmBtnText="Ok"
        btnSize=""
      >
        Delete Operation
      </ReactBSAlert>
    );
  };

  /**
   * SCRUM-1150: the container only describes the columns now.
   *
   * The Author column carries an `accessorFn` even though its cell renders a
   * button: TableWrapper's global filter only searches columns that expose a
   * value, and without it the box would silently skip the author name.
   *
   * SCRUM-2222: no useMemo keyed on `permission` and no ref for the posts any
   * more; the React Compiler memoises this, keyed on what the cells read. The
   * cells are named functions rather than arrows given a `displayName`, because
   * that assignment made the compiler skip the component.
   */
  const columns = (() => {
    const canEdit = !!(permission && (permission.publish || permission.edit));

    const AuthorCell = function SiteBlogAuthorCell({ row }) {
      const p = row.original;
      return (
        <Button
          className="px-0"
          color="link"
          type="button"
          onClick={() => viewAuthorInformation(p?.author)}
        >
          {p?.author?.name || "User Not Found"}
        </Button>
      );
    };

    const PictureCell = function SiteBlogPictureCell({ row }) {
      const p = row.original;
      if (!p?.picture) return "N/A";
      return (
        <img
          alt={p?.alt_pic || p?.title || "Post image"}
          className="avatar rounded-circle mr-3"
          src={p.picture}
          loading="lazy"
        />
      );
    };

    const TypeCell = function SiteBlogTypeCell({ row }) {
      return (
        <span className="text-muted">
          {row.original?.type ? (
            <b className="font-italic text-info">NEWS</b>
          ) : (
            "POST"
          )}
        </span>
      );
    };

    const base = [
      {
        accessorKey: "title",
        header: "Title",
        cell: ({ row }) => (
          <span className="text-muted">{row.original?.title}</span>
        ),
      },
      {
        id: "author",
        header: "Author",
        accessorFn: (row) => row?.author?.name || "",
        cell: AuthorCell,
      },
      { id: "picture", header: "Picture", cell: PictureCell },
      { id: "type", header: "Type", cell: TypeCell },
    ];

    if (!canEdit) return base;

    const ActionsCell = function SiteBlogActionsCell({ row }) {
      const p = row.original;
      return (
        <label className="custom-toggle">
          <input
            defaultChecked={p?.active}
            type="checkbox"
            onClick={(e) => onClickCheckBox(e, p)}
            aria-label={`Show ${p?.title || "this post"} on the blog`}
          />
          <span
            className="custom-toggle-slider rounded-circle"
            data-label-off="hide"
            data-label-on="show"
          />
        </label>
      );
    };

    return [...base, { id: "actions", header: "Actions", cell: ActionsCell }];
  })();

  return (
    <>
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      {alertNode}

      <SimpleHeader name="Manage Site Blog " parentName="Global Tools " />

      <SiteBlogView

        title={title}
        description={description}
        validation={validation}
        // buttons visibility & permissions
        viewAddButton={viewAddButton}
        viewUpdateButton={viewUpdateButton}
        permission={permission}
        hasNoBlog={hasNoBlog}
        // posts & author
        posts={sourcePosts}
        columns={columns}
        viewAuthors={viewAuthors}
        author={author}
        // handlers
        onChangeTitle={onChangeTitle}
        onChangeDescription={onChangeDescription}
        onClickAdd={onClickAdd}
        onClickUpdate={onClickUpdate}
        closeViewAuthor={closeViewAuthor}
        Link={Link}
      />
    </>
  );
}
